import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../zodios/client";
import { errorToast, successToast } from "../../../utils/toasts";
import { type UserInvitationUpsertRequest } from "../../../zodios/apiDefinitions/api/userManagement";

const STAFF_INVITE_ERROR_FORBIDDEN = "staff_invite_forbidden";
const STAFF_INVITE_ERROR_INVALID_DOMAIN = "staff_invite_invalid_domain";

export const useCreateUserInviteMutation = () => {
  const { t } = useTranslation(["user_management"]);
  const queryClient = useQueryClient();

  const localizedErrorMessage = (errorCode: string | undefined) => {
    switch (errorCode) {
      case STAFF_INVITE_ERROR_FORBIDDEN:
        return t(($) => $.user_management["Only Trellis staff can grant staff access."]);
      case STAFF_INVITE_ERROR_INVALID_DOMAIN:
        return t(($) => $.user_management["Staff access requires an @gotrellis.com email address."]);
      default:
        return t(($) => $.user_management["Failed to create user invitation"]);
    }
  };

  return useMutation({
    mutationFn: async (payload: UserInvitationUpsertRequest) => await apiClient.createUserInvitation(payload),
    onSuccess: (data) => {
      if (data.toLowerCase().includes("already invited")) {
        errorToast({ message: t(($) => $.user_management["User already invited"]) });
      } else if (data.toLowerCase().includes("already available")) {
        errorToast({ message: t(($) => $.user_management["User already registered"]) });
      } else {
        successToast({
          message: data,
        });
      }
      void queryClient.invalidateQueries({
        queryKey: [{ entity: "userInvitations" }],
      });
    },
    onError: (error: { response?: { data?: { error_code?: string } } }) => {
      errorToast({
        message: localizedErrorMessage(error.response?.data?.error_code),
      });
    },
  });
};
